/**
 * Table-Familie — Sprint-16-Brand.
 *
 * Editorial-Daten-Tabelle:
 *   - Container: bg-card + rounded-lg + border-line/50
 *   - Header (thead): bg-card-alt sticky + uppercase-tracked sans-Labels
 *   - Row-Hover: bg-card-alt/60 (kein Motion — Tabellen dürfen sich nicht bewegen)
 *   - Row-Border: 1px border-line/50, letzte Row ohne Border
 */
import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '@/shared/lib/cn';

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="border-line/50 bg-card w-full overflow-x-auto rounded-lg border">
      <table
        ref={ref}
        className={cn('w-full border-separate border-spacing-0 text-sm', className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('bg-card-alt sticky top-0 z-10', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={className} {...props} />);
TableBody.displayName = 'TableBody';

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn('hover:bg-card-alt/60 group transition-colors', className)}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'text-ink-soft border-line/50 border-b px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em]',
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('border-line/50 text-ink border-b px-4 py-3 group-last:border-0', className)}
      {...props}
    />
  ),
);
TableCell.displayName = 'TableCell';
